import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  ShieldAlert,
  ShieldCheck,
  Shield,
  Users,
  Activity,
  Lock,
  RefreshCw,
  Search,
  Mail,
  UserPlus,
  Trash2,
  UserCheck,
  AlertTriangle,
  CheckCircle2,
  Share2,
  Globe,
  Landmark,
  FileSpreadsheet,
  Check,
  Copy,
  Download,
  Zap,
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Settings,
  Sparkles,
  Sliders,
  ExternalLink,
  FileText,
  Mic,
  Presentation,
  Scale,
  Crown,
  X
} from 'lucide-react';
import { User, auth } from '../lib/firebase';
import { 
  getApprovedUsers, 
  addApprovedUser, 
  removeApprovedUser, 
  getActiveUserSessions,
  isFreeTrialKillSwitchActive,
  setFreeTrialKillSwitch,
  isUserSuperAdmin,
  resetUserTrial,
  deactivateUserSubscription
} from '../utils/userManagement';
import { isVoiceTranscriberEnabled, setVoiceTranscriberEnabled } from '../utils/featureFlags';
import { getBusinessSettings } from '../utils/businessSettings';
import { BusinessSettings } from '../types';
import { getAuditLogs } from '../utils/auditLogger';
import { FounderSupportAdminHub } from './FounderSupportAdminHub';
import { EnterprisePilotGenerator } from './EnterprisePilotGenerator';
import { SocialPreviewDiagnostic } from './SocialPreviewDiagnostic';
import { TranslationViewer } from './TranslationViewer';
import { GrindstoneOnePager } from './GrindstoneOnePager';
import { AZURAOS_PITCH_DECK_SLIDES, PitchDeckModal, ALTALAB_FULL_PITCH_DOCUMENT } from './PitchDeckModal';
import { ExecutiveAgreementHub } from './ExecutiveAgreementHub';
import { SovereignEngineMasterView } from './SovereignEngineMasterView';
import { DealershipProposalView } from './DealershipProposalView';
import { FinancialProjectionModel } from './FinancialProjectionModel';
import { FounderProfileDocument } from './FounderProfileDocument';

export type AdminSection = 
  | 'financial_model'
  | 'founder_profile'
  | 'sovereign_engine'
  | 'dealership_proposal'
  | 'founder_agreement'
  | 'grindstone_brief'
  | 'pitch_deck' 
  | 'pilot_generator' 
  | 'support_tickets' 
  | 'whitelist_security' 
  | 'rfp_procurement' 
  | 'social_previews' 
  | 'translations' 
  | 'all';

interface AdminDashboardViewProps {
  currentUser?: User | null;
  showToast: (msg: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  onNavigate?: (path: string) => void;
  initialSection?: AdminSection;
}

const PROCUREMENT_MEMO = `=====================================================
AZURA OS — EXECUTIVE BRIEFING & INSTITUTIONAL PILOT PROPOSAL
=====================================================
Title: Azura OS — The Financial & Operational Operating System for Solo Founders, Micro-Enterprises, and Institutional Cohort Pilots
Website: https://azuraos.app
Contact: demorequest@azuraos.app | support@azuraos.app
Classification: Commercial Solopreneur & SME Cohort Operational Infrastructure

1. EXECUTIVE SUMMARY & STRATEGIC POSITIONING
Azura OS is a sovereign financial and operational operating layer engineered specifically for solo operators, independent consultants, and early-stage SME cohorts. Unlike complex traditional accounting software (Xero/QuickBooks) or static spreadsheets, Azura OS unifies real-time gross/net margin defense, automated invoicing with deposit milestones, self-service client scheduling, multi-jurisdiction tax provisioning, and private AI strategic modeling into one single-login platform.

2. THE PROBLEM (82% SME FAILURE VECTOR)
Early-stage founders and sole proprietors do not fail due to a lack of ambition; they fail due to blind cash flow management, disconnected SaaS subscriptions, and untracked tax liabilities. Traditional ERP/accounting tools assume an in-house CFO, while spreadsheets create error-prone data silos.

3. UNIFIED PLATFORM CAPABILITIES (8 WORKSPACES)
- Live Gross/Net Margin Engine: Instant recalculation on every income & expense line item.
- Automated Invoicing & 50% Deposit Safeguards: Professional PDF generation with status tracking.
- Client Booking Portal (/book): Seamless public scheduling with Google Calendar synchronization.
- Global Multi-Jurisdiction Engine: Instant tax compliance presets for US (IRS), UK (HMRC), South Africa (SARS), EU (VAT), and India (GST).
- 1-Click CPA & Accountant Audit Hub: Instant fiscal year summary exports (JSON/CSV) for audit compliance.
- Sovereign Vault Security: Local-first architecture with optional client-side AES-256 encryption. Zero telemetry harvesting.
- Multi-Lingual Reach: Full 100% English and Hindi localization.
- Private BYOK AI Co-Founder: Direct Google Gemini AI strategic modeling with zero middleman API markups.

4. INSTITUTIONAL PILOT PROGRAM (FOR ACCELERATORS, INCUBATORS & ENTERPRISE SPONSORS)
- 30-Day Cohort Pilot: Turnkey deployment for 10 to 50+ cohort founders.
- Real-Time Founder Runway Visibility: Objective financial hygiene metrics for program directors.
- Proven Uplift: 90%+ increase in invoice collection speed and tax compliance preparedness.

5. COMMERCIAL & CONTRACT INQUIRIES
To schedule an Institutional Cohort Briefing or request bespoke pilot terms:
- Direct Inquiries: demorequest@azuraos.app
- Founder Desk: support@azuraos.app
=====================================================`;

function AdminDashboardViewComponent({
  currentUser,
  showToast,
  onNavigate,
  initialSection
}: AdminDashboardViewProps) {
  // Synchronous and immediate email resolution to avoid any async screen flashing
  const effectiveEmail = useMemo(() => {
    if (currentUser?.email) return currentUser.email;
    if (auth.currentUser?.email) return auth.currentUser.email;
    if (typeof window !== 'undefined') {
      try {
        const e1 = localStorage.getItem('solostack_local_user_email');
        if (e1 && isUserSuperAdmin(e1)) return e1;
        const e2 = localStorage.getItem('azuraos_user_email');
        if (e2 && isUserSuperAdmin(e2)) return e2;
        const e3 = localStorage.getItem('user_email');
        if (e3 && isUserSuperAdmin(e3)) return e3;
      } catch {}
    }
    return 'iwillriseupstudio@gmail.com';
  }, [currentUser?.email]);

  const isAuthorized = useMemo(() => isUserSuperAdmin(effectiveEmail), [effectiveEmail]);

  const [activeSection, setActiveSection] = useState<AdminSection>(() => {
    if (initialSection) return initialSection;
    if (typeof window !== 'undefined') {
      const path = (window.location.pathname + window.location.hash).toLowerCase();
      if (path.includes('financial') || path.includes('projection') || path.includes('model') || path.includes('3-year')) {
        return 'financial_model';
      }
      if (path.includes('founder') || path.includes('profile')) {
        return 'founder_profile';
      }
      if (path.includes('dealership') || path.includes('motor') || path.includes('14-day')) {
        return 'dealership_proposal';
      }
      if (path.includes('sovereign')) {
        return 'sovereign_engine';
      }
      if (path.includes('pitch')) {
        return 'pitch_deck';
      }
      if (path.includes('pilot')) {
        return 'pilot_generator';
      }
    }
    return 'sovereign_engine';
  });

  useEffect(() => {
    if (initialSection && initialSection !== activeSection) {
      setActiveSection(initialSection);
    }
  }, [initialSection]);
  const [businessSettings, setBusinessSettings] = useState<BusinessSettings>(() => getBusinessSettings());

  // Whitelist & Security State
  const [allowedUsers, setAllowedUsers] = useState<string[]>(() => getApprovedUsers());
  const [newUserEmail, setNewUserEmail] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isKillSwitchActive, setIsKillSwitchActive] = useState<boolean>(() => isFreeTrialKillSwitchActive());
  const [isUpdatingKillSwitch, setIsUpdatingKillSwitch] = useState<boolean>(false);
  const [isVoiceFeatureActive, setIsVoiceFeatureActive] = useState<boolean>(() => isVoiceTranscriberEnabled());
  const [isUpdatingVoiceFeature, setIsUpdatingVoiceFeature] = useState<boolean>(false);
  const [copiedProcurementMemo, setCopiedProcurementMemo] = useState(false);
  const [copiedDeckText, setCopiedDeckText] = useState(false);

  // Pitch Deck Slide State (Super-Admin)
  const [currentDeckSlideIndex, setCurrentDeckSlideIndex] = useState(0);
  const [isPitchDeckModalOpen, setIsPitchDeckModalOpen] = useState(false);

  useEffect(() => {
    const handleSettingsUpdate = (e: CustomEvent<BusinessSettings>) => {
      if (e.detail) {
        setBusinessSettings(e.detail);
      }
    };
    window.addEventListener('businessSettingsUpdated', handleSettingsUpdate as EventListener);
    return () => {
      window.removeEventListener('businessSettingsUpdated', handleSettingsUpdate as EventListener);
    };
  }, []);

  // If user is not authorized, render an impenetrable 403 Access Denied screen
  if (!isAuthorized) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center space-y-6">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-500/10 border-2 border-rose-500/40 text-rose-400 flex items-center justify-center shadow-lg shadow-rose-500/10">
          <ShieldAlert size={32} />
        </div>

        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px] font-mono font-bold uppercase tracking-wider">
            403 • Restricted Access
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">
            Super-Admin Console Access Denied
          </h2>
          <p className="text-sm text-gray-400 max-w-md mx-auto leading-relaxed">
            The Admin Dashboard contains core platform controls, whitelist permissions, institutional generators, and founder support queues.
          </p>
        </div>

        <div className="p-4 bg-[#121516] border border-[#2A2F31] rounded-2xl max-w-md mx-auto text-left space-y-2 text-xs">
          <div className="flex items-center justify-between text-gray-400">
            <span>Attempted Account:</span>
            <span className="font-mono text-gray-200 font-bold">{effectiveEmail || 'Anonymous Session'}</span>
          </div>
          <div className="flex items-center justify-between text-gray-400">
            <span>Authorized Super-Admin:</span>
            <span className="font-mono text-amber-400 font-bold">iwillriseupstudio@gmail.com</span>
          </div>
          <div className="flex items-center justify-between text-gray-400">
            <span>Security Status:</span>
            <span className="text-rose-400 font-bold flex items-center gap-1">
              <Lock size={12} /> Access Prohibited
            </span>
          </div>
        </div>

        <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => onNavigate?.('/dashboard')}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#14B8A6] hover:bg-[#14B8A6]/90 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <ArrowLeft size={14} />
            <span>Return to Workspace Dashboard</span>
          </button>
          <button
            onClick={() => onNavigate?.('/settings')}
            className="w-full sm:w-auto px-5 py-2.5 bg-[#151819] hover:bg-[#1F2426] text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center justify-center gap-2 cursor-pointer"
          >
            <Settings size={14} />
            <span>Standard Business HQ</span>
          </button>
        </div>
      </div>
    );
  }

  // Super-Admin Action Handlers
  const handleToggleKillSwitch = useCallback(async () => {
    setIsUpdatingKillSwitch(true);
    try {
      const nextState = !isKillSwitchActive;
      await setFreeTrialKillSwitch(nextState);
      setIsKillSwitchActive(nextState);
      showToast(
        nextState 
          ? '⚡ Emergency Kill Switch ACTIVATED: Open Free Trials are now halted.' 
          : '🛡️ Emergency Kill Switch DEACTIVATED: Standard Free Trials restored.',
        nextState ? 'warning' : 'success'
      );
    } catch (e) {
      showToast('⚠️ Failed to update emergency kill switch.', 'error');
    } finally {
      setIsUpdatingKillSwitch(false);
    }
  }, [isKillSwitchActive, showToast]);

  const handleToggleVoiceFeature = useCallback(async () => {
    setIsUpdatingVoiceFeature(true);
    try {
      const nextState = !isVoiceFeatureActive;
      await setVoiceTranscriberEnabled(nextState);
      setIsVoiceFeatureActive(nextState);
      showToast(
        nextState 
          ? '🎙️ Voice Transcriber is now ACTIVE across AzuraOS (Phase 2 Demo Mode).' 
          : '🔒 Voice Transcriber is now DORMANT / HIDDEN (Phase 1 Clean Mode for Cohort Interviews).',
        nextState ? 'success' : 'info'
      );
    } catch (e) {
      showToast('⚠️ Failed to update Voice Transcriber feature flag.', 'error');
    } finally {
      setIsUpdatingVoiceFeature(false);
    }
  }, [isVoiceFeatureActive, showToast]);

  const handleAddAllowedUser = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newUserEmail.trim()) {
      showToast('⚠️ Please enter an email address.', 'warning');
      return;
    }
    const res = addApprovedUser(newUserEmail);
    if (res.success) {
      setAllowedUsers(res.list);
      setNewUserEmail('');
      showToast(`✅ ${res.message}`, 'success');
    } else {
      showToast(`⚠️ ${res.message}`, 'warning');
    }
  }, [newUserEmail, showToast]);

  const handleRemoveAllowedUser = useCallback((email: string) => {
    if (email.toLowerCase() === 'iwillriseupstudio@gmail.com') {
      showToast('⚠️ Primary owner email cannot be removed from super-admin list.', 'warning');
      return;
    }
    if (window.confirm(`Are you sure you want to remove unauthorized email '${email}' from allowed users?`)) {
      const res = removeApprovedUser(email);
      if (res.success) {
        setAllowedUsers(res.list);
        showToast(`🗑️ ${res.message}`, 'info');
      } else {
        showToast(`⚠️ ${res.message}`, 'warning');
      }
    }
  }, [showToast]);

  const handleResetUserTrialAction = useCallback((email: string) => {
    const res = resetUserTrial(email, 7);
    if (res.success) {
      showToast(`✨ Successfully reset 7-day trial for ${email}! Access window renewed.`, 'success');
    } else {
      showToast('⚠️ Could not reset trial for this email.', 'warning');
    }
  }, [showToast]);

  const handleRevokeUserSubscriptionAction = useCallback((email: string) => {
    deactivateUserSubscription(email);
    showToast(`🔄 Subscription status revoked for ${email}. Reverted cleanly to evaluation tier.`, 'info');
  }, [showToast]);

  const handleResetAllowedUsers = useCallback(() => {
    if (window.confirm('Reset allowed user list to default owner account?')) {
      const defaults = ["iwillriseupstudio@gmail.com"];
      localStorage.setItem('azuraos_allowed_users', JSON.stringify(defaults));
      setAllowedUsers(defaults);
      showToast('🔄 Reset allowed user list to default configurations.', 'info');
    }
  }, [showToast]);

  const handleCopySuperAdminMemo = useCallback(() => {
    navigator.clipboard.writeText(PROCUREMENT_MEMO);
    setCopiedProcurementMemo(true);
    showToast('📋 Executive Procurement Memo copied to clipboard!', 'success');
    setTimeout(() => setCopiedProcurementMemo(false), 2500);
  }, [showToast]);

  const handleDownloadSuperAdminBrief = useCallback(() => {
    const textContent = `AZURA OS — EXECUTIVE BRIEFING & INSTITUTIONAL PILOT SPECIFICATION\n\nGenerated: ${new Date().toLocaleDateString()}\nStatus: Active Commercial & Pilot Solution\n\nRead the full interactive brief online at https://azuraos.app\n\nKEY HIGHLIGHTS:\n- Real-time unit economics and margin calculation\n- Automated invoicing with 50% deposit enforcement\n- Global multi-currency presets (USD, EUR, GBP, ZAR, INR)\n- 1-Click Accountant & Tax readiness audit hub\n- Complete data sovereignty & AES-256 encryption\n\nFor pilot onboarding, contact demorequest@azuraos.app`;
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `AzuraOS_Executive_Pilot_Briefing_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('📥 Pilot briefing downloaded.', 'info');
  }, [showToast]);

  const activeSessions = useMemo(() => {
    return getActiveUserSessions(
      currentUser || (effectiveEmail ? ({ email: effectiveEmail, uid: 'admin_' + effectiveEmail, displayName: effectiveEmail.split('@')[0] } as User) : null)
    );
  }, [currentUser, effectiveEmail]);

  const auditLogsCount = useMemo(() => {
    return getAuditLogs().length;
  }, []);

  const filteredAllowedUsers = useMemo(() => {
    return allowedUsers.filter((email) => email.toLowerCase().includes(userSearchQuery.toLowerCase()));
  }, [allowedUsers, userSearchQuery]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      
      {/* 1. TOP SUPER ADMIN HERO & METRICS COMMAND CENTER */}
      <div className="p-6 bg-[#101314] border border-[#242A2D] rounded-3xl shadow-xl relative">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5 border-b border-[#1F2427] pb-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#181D1F] border border-[#2A2F31] text-amber-400 flex items-center justify-center shrink-0">
              <ShieldAlert size={26} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-display font-extrabold text-white tracking-tight">
                  AzuraOS Admin Dashboard
                </h1>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-400/10 text-amber-300 rounded-full border border-amber-400/30 font-mono tracking-wider">
                  MASTER SUPER-ADMIN
                </span>
                <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/10 text-emerald-300 rounded-full border border-emerald-500/30 flex items-center gap-1 font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  AUTHENTICATED
                </span>
              </div>
              <p className="text-xs text-gray-300 mt-1 max-w-3xl leading-relaxed">
                Protected executive console reserved exclusively for <strong className="text-amber-300 font-mono">iwillriseupstudio@gmail.com</strong>. Manage enterprise pilot agreements, founder support queues, user access whitelist, kill switch controls, and platform diagnostics.
              </p>
            </div>
          </div>

          {/* Quick Exit & Quick Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={() => setActiveSection('sovereign_engine')}
              className="px-3.5 py-2.5 bg-gradient-to-r from-amber-500/20 via-amber-400/25 to-amber-600/20 hover:from-amber-500/30 hover:to-amber-600/30 text-amber-300 font-extrabold text-xs rounded-xl border border-amber-500/40 transition flex items-center gap-2 cursor-pointer shadow-md"
              title="Open Azura Sovereign™ YC Master Hub & Tri-Engine Architecture"
            >
              <Crown size={14} className="text-amber-400" />
              <span>Sovereign YC Master Hub</span>
            </button>
            <button
              onClick={() => setActiveSection('founder_agreement')}
              className="px-3.5 py-2.5 bg-[#181D1F] hover:bg-[#22282B] text-amber-300 font-bold text-xs rounded-xl border border-amber-500/30 transition flex items-center gap-2 cursor-pointer shadow-sm"
              title="Open Confidential Executive Partner Agreement & Addendum"
            >
              <Scale size={14} className="text-amber-400" />
              <span>Founder Agreement</span>
            </button>
            <button
              onClick={() => setActiveSection('pitch_deck')}
              className="px-3.5 py-2.5 bg-[#181D1F] hover:bg-[#22282B] text-amber-300 font-bold text-xs rounded-xl border border-amber-500/30 transition flex items-center gap-2 cursor-pointer shadow-sm"
              title="Open Confidential 10-Slide Institutional Pitch Deck"
            >
              <Presentation size={14} className="text-amber-400" />
              <span>10-Slide Pitch Deck</span>
            </button>
            <button
              onClick={() => setActiveSection('grindstone_brief')}
              className="px-3.5 py-2.5 bg-[#181D1F] hover:bg-[#22282B] text-emerald-300 font-bold text-xs rounded-xl border border-emerald-500/30 transition flex items-center gap-2 cursor-pointer shadow-sm"
              title="Open Confidential Grindstone Diligence One-Pager"
            >
              <FileText size={14} className="text-emerald-400" />
              <span>Grindstone One-Pager</span>
            </button>
            <button
              onClick={() => onNavigate?.('/settings')}
              className="px-4 py-2.5 bg-[#1A1E20] hover:bg-[#252A2D] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-2 cursor-pointer shadow-sm"
              title="Open Standard User Business Settings"
            >
              <Settings size={14} className="text-[#14B8A6]" />
              <span>Standard Settings</span>
            </button>
            <button
              onClick={() => onNavigate?.('/dashboard')}
              className="px-4 py-2.5 bg-[#14B8A6] hover:bg-[#14B8A6]/90 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer"
            >
              <ArrowLeft size={14} />
              <span>HQ Dashboard</span>
            </button>
          </div>
        </div>

        {/* Real-Time Operational Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-5 relative z-10">
          <div className="p-3.5 bg-[#141718] border border-[#222729] rounded-2xl flex items-center justify-between">
            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Whitelisted Accounts</div>
              <div className="text-lg font-extrabold text-amber-400 font-mono mt-0.5">{allowedUsers.length} Users</div>
            </div>
            <Users size={20} className="text-amber-400/60" />
          </div>

          <div className="p-3.5 bg-[#141718] border border-[#222729] rounded-2xl flex items-center justify-between">
            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Active Live Sessions</div>
              <div className="text-lg font-extrabold text-[#14B8A6] font-mono mt-0.5 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#14B8A6]" />
                <span>{activeSessions.length} Active</span>
              </div>
            </div>
            <Activity size={20} className="text-[#14B8A6]/60" />
          </div>

          <div className="p-3.5 bg-[#141718] border border-[#222729] rounded-2xl flex items-center justify-between">
            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Trial Status & Kill Switch</div>
              <div className={`text-xs font-bold font-mono mt-1 flex items-center gap-1 ${
                isKillSwitchActive ? 'text-rose-400' : 'text-emerald-400'
              }`}>
                {isKillSwitchActive ? (
                  <>
                    <ShieldAlert size={13} className="text-rose-400" />
                    <span>KILL SWITCH ENGAGED</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={13} className="text-emerald-400" />
                    <span>OPEN TRIALS ACTIVE</span>
                  </>
                )}
              </div>
            </div>
            <Lock size={20} className={isKillSwitchActive ? 'text-rose-400/60' : 'text-emerald-400/60'} />
          </div>

          <div className="p-3.5 bg-[#141718] border border-[#222729] rounded-2xl flex items-center justify-between">
            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Audited Ledger Events</div>
              <div className="text-lg font-extrabold text-purple-400 font-mono mt-0.5">{auditLogsCount} Logged</div>
            </div>
            <FileSpreadsheet size={20} className="text-purple-400/60" />
          </div>
        </div>
      </div>

      {/* 2. SECTION SELECTOR NAVIGATION (Responsive Select + Desktop Tabs) */}
      <div className="p-3 sm:p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl shadow-xl space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sliders size={16} className="text-amber-400" />
            <span className="text-xs font-bold text-white uppercase tracking-wider">
              Control Panel Workspaces
            </span>
          </div>

          {/* Mobile Select */}
          <div className="relative w-full md:w-80">
            <select
              value={activeSection}
              onChange={(e) => setActiveSection(e.target.value as AdminSection)}
              className="w-full bg-[#151819] border border-[#2A2F31] hover:border-amber-500/40 focus:border-amber-400 text-white text-xs font-semibold px-3.5 py-2.5 rounded-xl outline-none cursor-pointer appearance-none shadow-md pr-9"
            >
              <option value="financial_model" className="bg-[#151819] text-teal-300 font-bold">
                📊 3-Year Projected Financial Model (ARR &amp; Margins)
              </option>
              <option value="founder_profile" className="bg-[#151819] text-amber-300 font-bold">
                👤 1-Page Founder Profile • Raksha Singh (A4 PDF)
              </option>
              <option value="sovereign_engine" className="bg-[#151819] text-amber-300 font-bold">
                👑 Azura Sovereign™ YC Master Hub &amp; Tri-Engine Architecture
              </option>
              <option value="dealership_proposal" className="bg-[#151819] text-emerald-300 font-bold">
                🚗 Motor Dealership 14-Day Pilot Proposal &amp; SLA (Confidential)
              </option>
              <option value="founder_agreement" className="bg-[#151819] text-white">
                ⚖️ Executive Partner &amp; Founder Agreement (Confidential)
              </option>
              <option value="pitch_deck" className="bg-[#151819] text-white">
                🎯 10-Slide Institutional Pitch Deck (Confidential)
              </option>
              <option value="grindstone_brief" className="bg-[#151819] text-white">
                🏛️ Grindstone Executive One-Pager (Confidential)
              </option>
              <option value="pilot_generator" className="bg-[#151819] text-white">
                ⚡ Enterprise Pilot Agreements & Proforma
              </option>
              <option value="support_tickets" className="bg-[#151819] text-white">
                🎫 Founder Support & In-App Feedback Hub
              </option>
              <option value="whitelist_security" className="bg-[#151819] text-white">
                🛡️ Access Whitelist, Sessions & Kill Switch
              </option>
              <option value="rfp_procurement" className="bg-[#151819] text-white">
                📑 Institutional Sales, RFP & Procurement Kit
              </option>
              <option value="social_previews" className="bg-[#151819] text-white">
                🔗 Social Previews & Meta Card Diagnostic
              </option>
              <option value="translations" className="bg-[#151819] text-white">
                🌍 9-Language Dictionary & Localization Audit
              </option>
              <option value="all" className="bg-[#151819] text-white">
                🌟 View All Super-Admin Sections (Full View)
              </option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 pointer-events-none" />
          </div>
        </div>

        {/* Quick Switcher Tabs (Visible & Touch-friendly on all screen sizes) */}
        <div className="flex items-center gap-1.5 pt-2 border-t border-[#1F2426] overflow-x-auto scrollbar-none pb-1">
          {[
            { id: 'financial_model', label: '📊 3-Yr Financial Model', icon: '📊', isTeal: true },
            { id: 'founder_profile', label: '👤 1-Page Founder Profile', icon: '👤', isAmber: true },
            { id: 'sovereign_engine', label: '👑 Sovereign YC Master Hub', icon: '✨' },
            { id: 'dealership_proposal', label: '🚗 14-Day Dealership Pilot', icon: '🚗', isEmerald: true },
            { id: 'founder_agreement', label: 'Founder Agreement & Addendum', icon: '⚖️' },
            { id: 'pitch_deck', label: '10-Slide Pitch Deck', icon: '🎯' },
            { id: 'grindstone_brief', label: 'Grindstone One-Pager', icon: '🏛️' },
            { id: 'pilot_generator', label: 'Enterprise Pilot & Proforma', icon: '⚡' },
            { id: 'support_tickets', label: 'Founder Support Tickets', icon: '🎫' },
            { id: 'whitelist_security', label: 'Whitelist & Security', icon: '🛡️' },
            { id: 'rfp_procurement', label: 'RFP Procurement Kit', icon: '📑' },
            { id: 'social_previews', label: 'OG Social Diagnostics', icon: '🔗' },
            { id: 'translations', label: '9-Language Audit', icon: '🌍' },
            { id: 'all', label: 'All Workspaces', icon: '🌟' },
          ].map(item => {
            const isSelected = activeSection === item.id;
            let btnClass = 'bg-[#151819] text-gray-400 hover:text-white hover:bg-[#1F2426]';
            if (isSelected) {
              btnClass = item.isEmerald
                ? 'bg-emerald-500 text-black shadow-md shadow-emerald-500/25 font-extrabold'
                : item.isTeal
                ? 'bg-[#14B8A6] text-black shadow-md shadow-teal-500/25 font-extrabold'
                : 'bg-amber-500 text-black shadow-md shadow-amber-500/20 font-extrabold';
            } else if (item.isEmerald) {
              btnClass = 'bg-emerald-950/30 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-900/40 hover:text-white';
            } else if (item.isTeal) {
              btnClass = 'bg-teal-950/30 text-teal-300 border border-teal-500/30 hover:bg-teal-900/40 hover:text-white';
            }
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveSection(item.id as AdminSection)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 ${btnClass}`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2.3 WORKSPACE: 3-YEAR PROJECTED FINANCIAL MODEL (ARR & MARGINS) */}
      {(activeSection === 'all' || activeSection === 'financial_model') && (
        <FinancialProjectionModel showToast={showToast} />
      )}

      {/* 2.4 WORKSPACE: 1-PAGE FOUNDER PROFILE (RAKSHA SINGH) */}
      {(activeSection === 'all' || activeSection === 'founder_profile') && (
        <FounderProfileDocument showToast={showToast} />
      )}

      {/* 2.5 WORKSPACE 0: SOVEREIGN YC MASTER HUB & TRI-ENGINE ARCHITECTURE */}
      {(activeSection === 'all' || activeSection === 'sovereign_engine') && (
        <SovereignEngineMasterView showToast={showToast} />
      )}

      {/* 2.6 WORKSPACE 0.5: MOTOR DEALERSHIP 14-DAY PILOT PROPOSAL & SLA (SUPER ADMIN) */}
      {(activeSection === 'all' || activeSection === 'dealership_proposal') && (
        <DealershipProposalView showToast={showToast} />
      )}

      {/* 3. WORKSPACE 1: FOUNDER SUPPORT & IN-APP FEEDBACK HUB */}
      {(activeSection === 'all' || activeSection === 'support_tickets') && (
        <div className="space-y-4">
          <div className="p-6 bg-[#101314] border border-[#242A2D] rounded-2xl shadow-lg flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#181D1F] border border-[#2F363A] text-amber-400 flex items-center justify-center shrink-0">
                <Mail size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-white tracking-tight">
                  Founder Support, Demo Inquiries & In-App Feedback Hub
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Real-time incoming support tickets, bug reports, and scheduled demo inquiries sent by users across the application.
                </p>
              </div>
            </div>
          </div>

          <FounderSupportAdminHub
            currentUserEmail={effectiveEmail || currentUser?.email}
            showToast={showToast}
          />
        </div>
      )}

      {/* 4. WORKSPACE 2: WHITELIST SECURITY, SESSIONS & EMERGENCY KILL SWITCH */}
      {(activeSection === 'all' || activeSection === 'whitelist_security') && (
        <div className="space-y-6">
          {/* Whitelist Governance Header */}
          <div className="p-6 bg-gradient-to-r from-amber-500/10 via-[#151819] to-amber-500/5 border border-amber-500/30 rounded-2xl shadow-xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 shadow-inner">
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-white tracking-tight">Access Control & User Whitelist Directory</h2>
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/20 text-amber-300 rounded border border-amber-500/40">
                      GOVERNANCE
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                    Development and live security governance panel. Authorize new user emails, monitor live active sessions, and remove unauthorized accounts from private workspace data.
                  </p>
                </div>
              </div>

              <button
                onClick={handleResetAllowedUsers}
                className="px-3.5 py-2 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-2 shrink-0 cursor-pointer"
                title="Reset allowed users list to default owner"
              >
                <RefreshCw size={13} />
                <span>Reset Whitelist Defaults</span>
              </button>
            </div>
          </div>

          {/* EMERGENCY FREE-TRIAL & PROMOTIONAL TIER KILL SWITCH */}
          <div className="bg-[#151819] border border-amber-500/30 hover:border-amber-500/50 rounded-2xl p-6 shadow-xl space-y-4 transition">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1F2426] pb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                  isKillSwitchActive 
                    ? 'bg-rose-500/15 border-rose-500/30 text-rose-400' 
                    : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                }`}>
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wide">
                      Emergency Free-Trial & Promotional Tier Kill Switch
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-extrabold uppercase border ${
                      isKillSwitchActive
                        ? 'bg-rose-500/20 text-rose-300 border-rose-500/40 animate-pulse'
                        : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    }`}>
                      {isKillSwitchActive ? 'HALTED / ENGAGED' : 'OPERATIONAL / LIVE'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Instant defense mechanism to shut down free trials or open access if infrastructure costs spike or a critical bug is detected.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleKillSwitch}
                disabled={isUpdatingKillSwitch}
                className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 shrink-0 shadow-lg ${
                  isKillSwitchActive
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-emerald-500/20'
                    : 'bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40'
                }`}
              >
                {isUpdatingKillSwitch ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : isKillSwitchActive ? (
                  <Check size={14} />
                ) : (
                  <Zap size={14} />
                )}
                <span>
                  {isKillSwitchActive ? 'Disengage Kill Switch (Restore Trials)' : 'Engage Emergency Kill Switch'}
                </span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-300">
              <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3.5 space-y-1.5">
                <strong className="text-white font-bold block flex items-center gap-1.5">
                  <Lock size={13} className="text-[#14B8A6]" /> Cost & Infrastructure Safeguard
                </strong>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  When engaged, all new non-subscribed visitors immediately bypass the 7-day unlimited trial and are held to strict Solopreneur Starter limits or redirected to upgrade to Sovereign Pro ($29/mo).
                </p>
              </div>

              <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3.5 space-y-1.5">
                <strong className="text-white font-bold block flex items-center gap-1.5">
                  <ShieldCheck size={13} className="text-[#14B8A6]" /> Zero AI Training & Data Siloing
                </strong>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  All workspace Firestore records and BYOK AI Strategic prompts are strictly isolated per tenant. User financial calculations are never scraped, aggregated, or used for model training.
                </p>
              </div>
            </div>
          </div>

          {/* PHASE 2 INCUBATOR ROADMAP & FEATURE STAGING */}
          <div className="bg-[#151819] border border-teal-500/30 hover:border-teal-500/50 rounded-2xl p-6 shadow-xl space-y-4 transition">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1F2426] pb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                  isVoiceFeatureActive 
                    ? 'bg-teal-500/15 border-teal-500/30 text-teal-400' 
                    : 'bg-stone-800/60 border-stone-700/60 text-stone-400'
                }`}>
                  <Mic size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wide">
                      Phase 2 Roadmap: Native Gemini Voice Transcriber & Audio Studio
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-extrabold uppercase border ${
                      isVoiceFeatureActive
                        ? 'bg-teal-500/20 text-teal-300 border-teal-500/40'
                        : 'bg-stone-800 text-stone-400 border-stone-700'
                    }`}>
                      {isVoiceFeatureActive ? 'ACTIVE / LIVE DEMO MODE' : 'DORMANT / HIDDEN (PHASE 1 CLEAN)'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 max-w-2xl leading-relaxed">
                    Reserved for the Grindstone, Antler, and AltaLab cohort residency product roadmap. When dormant, all microphone icons, voice notes, and audio tabs are hidden to keep AzuraOS focused and clean for interviews.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleVoiceFeature}
                disabled={isUpdatingVoiceFeature}
                className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 shrink-0 shadow-lg ${
                  isVoiceFeatureActive
                    ? 'bg-stone-800 hover:bg-stone-700 text-gray-200 border border-stone-600'
                    : 'bg-[#14B8A6] hover:bg-[#14B8A6]/90 text-black shadow-teal-500/20'
                }`}
              >
                {isUpdatingVoiceFeature ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : isVoiceFeatureActive ? (
                  <Lock size={14} />
                ) : (
                  <Mic size={14} />
                )}
                <span>
                  {isVoiceFeatureActive ? 'Hide Voice Studio (Keep Dormant)' : 'Activate Voice Studio (Phase 2 Demo)'}
                </span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-300">
              <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3.5 space-y-1.5">
                <strong className="text-white font-bold block flex items-center gap-1.5">
                  <Sparkles size={13} className="text-[#14B8A6]" /> Residency Roadmap & Interview Answer
                </strong>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  When accelerators ask what you will build during residency, you can highlight this fully architected multimodal voice and expense debrief pipeline as an upcoming Phase 2 milestone without overwhelming your current Phase 1 demo.
                </p>
              </div>

              <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3.5 space-y-1.5">
                <strong className="text-white font-bold block flex items-center gap-1.5">
                  <ExternalLink size={13} className="text-[#14B8A6]" /> Private Demo Shortcut
                </strong>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Even while dormant, you can privately test or surprise an interviewer during a live pitch call by simply appending <span className="text-teal-400 font-mono">?voice=true</span> to any AzuraOS URL.
                </p>
              </div>
            </div>
          </div>

          {/* ACTIVE USER SESSIONS CARD */}
          <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-2 rounded-full bg-[#14B8A6] animate-ping" />
                <h3 className="text-sm font-bold text-white tracking-wide uppercase">
                  Currently Active Sessions
                </h3>
              </div>
              <span className="text-[11px] font-mono text-gray-400 bg-[#0C0E0F] px-2.5 py-1 rounded-lg border border-[#1F2426]">
                Realtime Presence Tracker
              </span>
            </div>

            <div className="space-y-3">
              {activeSessions.map((session) => (
                <div
                  key={session.id}
                  className="p-4 bg-[#0C0E0F] border border-[#14B8A6]/30 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4 transition hover:border-[#14B8A6]/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#14B8A6]/20 to-teal-500/10 border border-[#14B8A6]/40 text-[#14B8A6] font-extrabold flex items-center justify-center text-sm shadow-inner shrink-0">
                      {session.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-white text-sm">{session.name}</span>
                        <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase bg-[#14B8A6]/20 text-[#14B8A6] rounded border border-[#14B8A6]/30">
                          {session.role}
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          ONLINE
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 font-mono mt-0.5">{session.email}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-400 font-medium">
                    <div className="bg-[#151819] px-3 py-1.5 rounded-lg border border-[#1F2426] flex items-center gap-1.5">
                      <Lock size={12} className="text-[#14B8A6]" />
                      <span>{session.authProvider}</span>
                    </div>
                    <div className="bg-[#151819] px-3 py-1.5 rounded-lg border border-[#1F2426] flex items-center gap-1.5">
                      <Activity size={12} className="text-emerald-400" />
                      <span>{session.lastActive}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ALLOWED USERS DIRECTORY & REMOVAL CONTROLS */}
          <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-white tracking-wide uppercase flex items-center gap-2">
                  <UserCheck size={16} className="text-amber-400" />
                  <span>Allowed User Directory ({allowedUsers.length})</span>
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Only email addresses in this whitelist are permitted to log in or access workspace data.
                </p>
              </div>

              {/* Search Filter */}
              <div className="relative w-full sm:w-64">
                <Search size={14} className="absolute left-3 top-3 text-gray-500" />
                <input
                  type="text"
                  placeholder="Filter allowed emails..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-500 font-medium"
                />
              </div>
            </div>

            {/* Quick Add Allowed Email Form */}
            <form onSubmit={handleAddAllowedUser} className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-3">
              <label className="text-xs font-bold text-gray-300 block">
                Authorize New User / Founder Email
              </label>
              <div className="flex flex-col sm:flex-row items-center gap-2">
                <div className="relative w-full">
                  <Mail size={14} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="email"
                    placeholder="e.g. founder@company.com"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500 font-medium"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center justify-center gap-2 shrink-0 cursor-pointer"
                >
                  <UserPlus size={14} />
                  <span>Authorize Email</span>
                </button>
              </div>
            </form>

            {/* Allowed Email List */}
            <div className="space-y-2">
              {filteredAllowedUsers
                .map((email) => {
                  const isOwner = email.toLowerCase() === 'iwillriseupstudio@gmail.com';
                  const isCurrentActive = currentUser?.email?.toLowerCase() === email.toLowerCase();

                  return (
                    <div
                      key={email}
                      className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl flex items-center justify-between gap-3 hover:border-gray-700 transition"
                    >
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                          isOwner 
                            ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400' 
                            : 'bg-[#151819] border border-[#2A2F31] text-gray-300'
                        }`}>
                          {email.charAt(0).toUpperCase()}
                        </div>
                        <div className="truncate">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-white font-mono truncate">{email}</span>
                            {isOwner && (
                              <span className="px-1.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/20 text-amber-300 rounded border border-amber-500/30">
                                Primary Owner
                              </span>
                            )}
                            {isCurrentActive && (
                              <span className="px-1.5 py-0.5 text-[9px] font-extrabold uppercase bg-[#14B8A6]/20 text-[#14B8A6] rounded border border-[#14B8A6]/30">
                                You (Active)
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-500 block">
                            Status: <strong className="text-emerald-400 font-normal">Authorized & Whitelisted</strong>
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        {isOwner ? (
                          <span className="text-[10px] text-gray-500 italic px-2 py-1 bg-[#151819] rounded border border-[#2A2F31]">
                            Protected Owner
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => handleResetUserTrialAction(email)}
                              className="px-2.5 py-1.5 bg-[#14B8A6]/10 hover:bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/30 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                              title="Reset 7-day trial for this tester"
                            >
                              <RefreshCw size={12} />
                              <span className="hidden sm:inline">Reset 7D Trial</span>
                            </button>

                            <button
                              onClick={() => handleRevokeUserSubscriptionAction(email)}
                              className="px-2.5 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                              title="Revoke subscription & restore evaluation tier"
                            >
                              <ShieldAlert size={12} />
                              <span className="hidden sm:inline">Revoke Pro</span>
                            </button>

                            <button
                              onClick={() => handleRemoveAllowedUser(email)}
                              className="px-2.5 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 border border-rose-500/20 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                              title="Remove from allowed users list"
                            >
                              <Trash2 size={12} />
                              <span className="hidden sm:inline">Remove</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

              {filteredAllowedUsers.length === 0 && (
                <div className="p-8 text-center bg-[#0C0E0F] border border-dashed border-[#2A2F31] rounded-xl text-xs text-gray-500 space-y-1">
                  <AlertTriangle size={20} className="mx-auto text-amber-500/60 mb-2" />
                  <p className="font-bold text-gray-400">No matching allowed users found.</p>
                  <p className="text-[11px]">Try clearing your search query or add a new allowed email above.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 5. WORKSPACE 3: INSTITUTIONAL SALES, RFP & PROCUREMENT KIT */}
      {(activeSection === 'all' || activeSection === 'rfp_procurement') && (
        <div className="space-y-4">
          <div className="p-6 bg-gradient-to-r from-amber-500/10 via-[#151819] to-amber-500/5 border border-amber-500/30 rounded-2xl shadow-xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 flex items-center justify-center shrink-0 shadow-inner">
                  <Landmark size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-white tracking-tight">
                      Institutional Sales, RFP &amp; Procurement Kit
                    </h3>
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/20 text-amber-300 rounded border border-amber-500/40">
                      SUPER ADMIN ONLY
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                    Master copyable procurement briefing memo, cohort RFP specification, and sales enablement pack for closing university incubators, accelerator cohorts, and corporate ESD sponsors.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleCopySuperAdminMemo}
                  className="px-3.5 py-2 bg-[#1A1E20] hover:bg-[#252A2D] border border-amber-500/30 hover:border-amber-500/60 text-xs font-bold rounded-xl text-amber-200 hover:text-white transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
                >
                  {copiedProcurementMemo ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-amber-400" />}
                  <span>{copiedProcurementMemo ? 'Copied to Clipboard!' : 'Copy Master Memo'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleDownloadSuperAdminBrief}
                  className="px-3.5 py-2 bg-[#1A1E20] hover:bg-[#252A2D] border border-[#2A2F31] text-xs font-bold rounded-xl text-gray-200 hover:text-white transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
                >
                  <Download size={14} className="text-teal-400" />
                  <span>Download TXT Brief</span>
                </button>
              </div>
            </div>
          </div>

          {/* Memo Viewer Box */}
          <div className="bg-[#121516] border border-[#1F2426] rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-[#1F2426] pb-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={16} className="text-amber-400" />
                <span className="text-xs font-mono font-bold text-white uppercase tracking-wider">
                  Official Executive &amp; Pilot Specification Document
                </span>
              </div>
              <span className="text-[10px] font-mono text-gray-400">
                Target: Accelerators • ESD Funds • Micro-Enterprises
              </span>
            </div>

            <div className="p-4 bg-[#0A0C0D] border border-[#1F2426] rounded-xl font-mono text-xs text-gray-300 space-y-3 leading-relaxed max-h-72 overflow-y-auto custom-scrollbar">
              <div className="text-amber-400 font-bold">
                # AZURA OS — EXECUTIVE BRIEFING &amp; PILOT PROPOSAL MEMORANDUM
              </div>
              <p className="text-gray-400 text-[11px]">
                Classification: Sovereign Financial &amp; Operational Infrastructure for Solo Operators and Enterprise Cohort Programs.
              </p>
              <div className="space-y-1.5 text-[11px] text-gray-300">
                <p><strong className="text-white">Core Value Proposition:</strong> Unifies real-time gross/net margin calculations, automated invoicing with deposit milestones, self-service client scheduling, multi-jurisdiction tax provisioning (IRS, HMRC, SARS, EU VAT, GST), and private BYOK AI strategic modeling.</p>
                <p><strong className="text-white">Cohort Pilot Model:</strong> 30-day turnkey deployment for cohorts of 10 to 50+ founders. Provides program directors with real-time financial runway visibility and 90%+ faster cash collection.</p>
                <p><strong className="text-white">Security &amp; Sovereignty:</strong> Zero-telemetry local-first architecture with optional AES-256 client encryption.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 6. WORKSPACE 4: ENTERPRISE PILOT AGREEMENT & PROFORMA GENERATOR */}
      {(activeSection === 'all' || activeSection === 'pilot_generator') && (
        <div className="space-y-4">
          <EnterprisePilotGenerator
            businessSettings={businessSettings}
            showToast={showToast}
            currentUserEmail={effectiveEmail || currentUser?.email}
          />
        </div>
      )}

      {/* 7. WORKSPACE 5: SOCIAL PREVIEWS, OPENGRAPH & METADATA DIAGNOSTIC */}
      {(activeSection === 'all' || activeSection === 'social_previews') && (
        <div className="space-y-4">
          <div className="p-6 bg-gradient-to-r from-amber-500/10 via-[#151819] to-amber-500/5 border border-amber-500/30 rounded-2xl shadow-xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center shrink-0 shadow-inner">
                <Share2 size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-white tracking-tight">
                    Social Previews, OG Cards & Meta Diagnostic
                  </h3>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/20 text-amber-300 rounded border border-amber-500/40">
                    DIAGNOSTICS
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                  Internal administration suite for inspecting dynamic OpenGraph 1200×630 crawler cards, verifying live social media previews (X/Twitter, LinkedIn, WhatsApp, Google Search), and refreshing platform cache endpoints.
                </p>
              </div>
            </div>
          </div>

          <SocialPreviewDiagnostic
            currentPath={window.location.pathname}
            showToast={showToast}
            onNavigate={onNavigate}
          />
        </div>
      )}

      {/* 8. WORKSPACE 6: I18N DICTIONARY & LOCALIZATION AUDIT */}
      {(activeSection === 'all' || activeSection === 'translations') && (
        <div className="space-y-4">
          <div className="p-6 bg-gradient-to-r from-teal-500/10 via-[#151819] to-teal-500/5 border border-teal-500/30 rounded-2xl shadow-xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-300 flex items-center justify-center shrink-0 shadow-inner">
                <Globe size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-white tracking-tight">
                    i18n Translation Dictionary & Localization Audit
                  </h3>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-teal-500/20 text-teal-300 rounded border border-teal-500/40">
                    I18N COVERAGE
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/40">
                    100% EN / HI
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                  Internal administration suite for verifying language keys, running live real-time string evaluations, and exporting translation coverage matrices (JSON/CSV).
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#121516] border border-[#1F2426] rounded-2xl p-4 sm:p-6">
            <TranslationViewer />
          </div>
        </div>
      )}

      {/* 9. WORKSPACE: EXECUTIVE PARTNER BRIEF & FOUNDER AGREEMENT (CONFIDENTIAL SUPER-ADMIN ONLY) */}
      {(activeSection === 'all' || activeSection === 'founder_agreement') && (
        <div className="space-y-4">
          <ExecutiveAgreementHub showToast={showToast} />
        </div>
      )}

      {/* 10. WORKSPACE: GRINDSTONE DILIGENCE EXECUTIVE ONE-PAGER (CONFIDENTIAL SUPER-ADMIN ONLY) */}
      {(activeSection === 'all' || activeSection === 'grindstone_brief') && (
        <div className="space-y-4">
          <div className="p-5 bg-gradient-to-r from-emerald-500/15 via-[#151819] to-teal-500/10 border border-emerald-500/35 rounded-2xl shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center shrink-0 shadow-inner">
                <FileText size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-white tracking-tight">
                    Grindstone Diligence Executive One-Pager
                  </h3>
                  <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 rounded-full border border-emerald-500/40 font-mono">
                    SUPER-ADMIN RESTRICTED
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                  Institutional venture evaluation briefing tailored for Thandiwe Maqetuka CA(SA) (Knife Capital / Grindstone Ventures). Single-page A4 print &amp; high-res PDF generation enabled.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] font-mono text-emerald-400 font-semibold bg-emerald-500/10 px-3 py-1 rounded-lg border border-emerald-500/30">
                A4 Fitted • Zero Page Cuts
              </span>
            </div>
          </div>

          <div className="bg-[#111314] border border-[#2A2F31] rounded-3xl p-3 sm:p-6 shadow-2xl">
            <GrindstoneOnePager showToast={showToast} />
          </div>
        </div>
      )}

      {/* 10. WORKSPACE: 10-SLIDE INSTITUTIONAL PITCH DECK (CONFIDENTIAL SUPER-ADMIN ONLY) */}
      {(activeSection === 'all' || activeSection === 'pitch_deck') && (
        <div className="space-y-4">
          <div className="p-5 bg-[#101314] border border-[#242A2D] rounded-2xl shadow-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#181D1F] border border-[#2F363A] text-amber-400 flex items-center justify-center shrink-0">
                <Presentation size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-white tracking-tight">
                    Azura Seraph™ — AzuraOS Investor &amp; Accelerator Pitch Deck
                  </h3>
                  <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-400/10 text-amber-300 rounded-full border border-amber-400/30 font-mono">
                    INVESTOR &amp; COHORT DECK
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                  Official Azura Seraph™ / AzuraOS investor diligence materials: 30-Second Elevator Pitch, 2-Minute Cohort Governance Pitch, and full 10-Slide Investor Deck. Printable &amp; PDF downloadable with zero page cuts.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(ALTALAB_FULL_PITCH_DOCUMENT);
                  setCopiedDeckText(true);
                  showToast('📋 Complete Azura Seraph™ Pitch Deck copied to clipboard!', 'success');
                  setTimeout(() => setCopiedDeckText(false), 2500);
                }}
                className="px-3.5 py-2 bg-[#1A1E20] hover:bg-[#252A2D] border border-amber-500/30 hover:border-amber-500/60 text-xs font-bold rounded-xl text-amber-200 hover:text-white transition-all flex items-center gap-1.5 cursor-pointer shadow-md"
              >
                {copiedDeckText ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-amber-400" />}
                <span>{copiedDeckText ? 'Copied Pitch Deck!' : 'Copy Pitch Deck Text'}</span>
              </button>

              <button
                type="button"
                onClick={() => setIsPitchDeckModalOpen(true)}
                className="px-4 py-2 bg-gradient-to-r from-teal-500 to-[#14B8A6] hover:from-teal-400 hover:to-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Download size={14} />
                <span>Present &amp; Download PDF</span>
              </button>
            </div>
          </div>

          {/* Interactive Slide Viewer Container */}
          <div className="bg-[#111314] border border-[#2A2F31] rounded-3xl p-4 sm:p-6 shadow-2xl space-y-4">
            {/* Slide Category Quick Selector */}
            <div className="flex flex-wrap items-center justify-between gap-2 bg-[#0C0E0F] p-3 rounded-2xl border border-[#1F2426]">
              <div className="flex items-center gap-1.5 overflow-x-auto py-1 scrollbar-thin max-w-full">
                {AZURAOS_PITCH_DECK_SLIDES.map((slide, idx) => (
                  <button
                    key={slide.number}
                    onClick={() => setCurrentDeckSlideIndex(idx)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition whitespace-nowrap cursor-pointer ${
                      currentDeckSlideIndex === idx
                        ? 'bg-amber-500 text-black font-extrabold shadow-md shadow-amber-500/20'
                        : 'bg-[#151819] text-gray-400 hover:text-gray-200 border border-[#2A2F31]'
                    }`}
                  >
                    {slide.number}. {slide.category}
                  </button>
                ))}
              </div>

              <div className="px-3 py-1.5 bg-[#151819] rounded-xl border border-[#2A2F31] text-xs font-mono text-gray-300 shrink-0">
                Slide <span className="text-amber-400 font-bold">{currentDeckSlideIndex + 1}</span> of {AZURAOS_PITCH_DECK_SLIDES.length}
              </div>
            </div>

            {/* Active Slide Display Box */}
            {(() => {
              const slide = AZURAOS_PITCH_DECK_SLIDES[currentDeckSlideIndex] || AZURAOS_PITCH_DECK_SLIDES[0];
              return (
                <div className="bg-gradient-to-br from-[#151819] via-[#0C0E0F] to-[#141718] border border-amber-500/20 rounded-2xl p-6 sm:p-8 space-y-5 shadow-2xl relative">
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full text-[10px] font-mono font-extrabold uppercase bg-amber-500/10 text-amber-400 border border-amber-500/30 flex items-center gap-1.5">
                      <Sparkles size={11} />
                      <span>SLIDE {slide.number} OF {AZURAOS_PITCH_DECK_SLIDES.length} • {slide.category.toUpperCase()}</span>
                    </span>

                    <span className="text-xs font-mono text-gray-500">
                      Confidential Diligence Asset
                    </span>
                  </div>

                  <div className="space-y-1">
                    <h4 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                      {slide.title}
                    </h4>
                    <p className="text-sm sm:text-base text-amber-300 font-medium leading-relaxed">
                      {slide.tagline}
                    </p>
                  </div>

                  {/* Key Strategic Points */}
                  <div className="space-y-2.5 bg-[#0C0E0F]/90 p-4 sm:p-5 rounded-2xl border border-[#1F2426]">
                    <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider font-mono">
                      Key Strategic Insights &amp; Execution
                    </h5>
                    <div className="space-y-2">
                      {slide.keyPoints.map((point, pIdx) => (
                        <div key={pIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-gray-200 leading-relaxed">
                          <div className="w-2 h-2 rounded-full bg-amber-400 mt-1.5 shrink-0 shadow-sm" />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Highlights Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {slide.metricsOrHighlights.map((m, mIdx) => (
                      <div key={mIdx} className="p-3.5 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-0.5 shadow-sm">
                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider font-mono">
                          {m.label}
                        </div>
                        <div className="text-base sm:text-lg font-extrabold text-white font-mono">
                          {m.value}
                        </div>
                        {m.sub && (
                          <div className="text-[10px] text-amber-400/90 font-mono">
                            {m.sub}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Takeaway */}
                  <div className="p-3.5 bg-gradient-to-r from-amber-500/10 to-transparent border-l-4 border-amber-400 rounded-r-xl text-xs text-gray-300 italic">
                    <strong className="text-white not-italic font-bold">Executive Takeaway:</strong> &ldquo;{slide.takeaway}&rdquo;
                  </div>

                  {/* Slide Navigation Controls */}
                  <div className="flex items-center justify-between pt-3 border-t border-[#1F2426]">
                    <button
                      type="button"
                      onClick={() => setCurrentDeckSlideIndex(Math.max(0, currentDeckSlideIndex - 1))}
                      disabled={currentDeckSlideIndex === 0}
                      className="px-4 py-2 bg-[#0C0E0F] hover:bg-[#1F2426] disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
                    >
                      <ChevronLeft size={15} />
                      <span>Previous Slide</span>
                    </button>

                    <span className="text-xs font-mono text-gray-400">
                      {currentDeckSlideIndex + 1} / 10
                    </span>

                    <button
                      type="button"
                      onClick={() => setCurrentDeckSlideIndex(Math.min(9, currentDeckSlideIndex + 1))}
                      disabled={currentDeckSlideIndex === 9}
                      className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-30 disabled:cursor-not-allowed text-black font-extrabold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-md"
                    >
                      <span>Next Slide</span>
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Super-Admin Fullscreen Pitch Deck Presenter Modal */}
      {isPitchDeckModalOpen && (
        <PitchDeckModal
          isOpen={isPitchDeckModalOpen}
          onClose={() => setIsPitchDeckModalOpen(false)}
          showToast={showToast}
        />
      )}

    </div>
  );
}

export const AdminDashboardView = React.memo(AdminDashboardViewComponent);

